import axios from 'axios';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useTheme, alpha } from '@mui/material/styles';
import trainingDataLoader from './trainingDataLoader';
import { useParams } from 'react-router-dom';
import { SET_TOUR_RUNNING, SET_ACTIVE_TOUR_SCREEN, SET_ACTIVE_TOUR_STEP,
         SET_TOUR_RECORD_IDS } from '../actions/types';
import { EVORA_PURPLE, DARK_BLUE, SKY_BLUE } from '../theme/colors';

// Theme-driven Joyride styling. Evora brand accents, palette-aware
// surfaces/text so the tour reads correctly in light and dark modes.
function buildJoyrideStyles(theme) {
    const isDark = theme.palette.mode === 'dark';
    const accent = EVORA_PURPLE;
    const accentSoft = isDark ? SKY_BLUE : EVORA_PURPLE;

    return {
        options: {
            primaryColor: accent,
            textColor: theme.palette.text.primary,
            backgroundColor: theme.palette.background.paper,
            arrowColor: theme.palette.background.paper,
            overlayColor: isDark
                ? 'rgba(0, 0, 0, 0.72)'
                : alpha(DARK_BLUE, 0.55),
            spotlightShadow: isDark
                ? '0 0 0 4px rgba(200, 219, 238, 0.25), 0 0 32px 4px rgba(0, 0, 0, 0.6)'
                : `0 0 0 4px ${alpha(accent, 0.35)}, 0 0 32px 6px ${alpha(accent, 0.25)}`,
            zIndex: 10000,
            width: 380
        },
        tooltip: {
            borderRadius: 12,
            padding: 0,
            boxShadow: isDark
                ? '0 12px 32px rgba(0, 0, 0, 0.55)'
                : '0 12px 32px rgba(27, 34, 58, 0.18)',
            border: `1px solid ${theme.palette.divider}`,
            fontFamily: theme.typography.fontFamily,
            overflow: 'hidden'
        },
        tooltipContainer: {
            textAlign: 'left'
        },
        tooltipTitle: {
            fontSize: 17,
            fontWeight: 600,
            color: accentSoft,
            margin: 0,
            padding: '18px 24px 4px',
            lineHeight: 1.3,
            letterSpacing: 0.2
        },
        tooltipContent: {
            fontSize: 14,
            color: theme.palette.text.primary,
            lineHeight: 1.6,
            padding: '8px 24px 16px'
        },
        tooltipFooter: {
            margin: 0,
            padding: '12px 20px',
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: isDark
                ? alpha(theme.palette.background.default, 0.4)
                : alpha(theme.palette.primary.main, 0.04)
        },
        tooltipFooterSpacer: { flex: 1 },
        buttonNext: {
            backgroundColor: accent,
            backgroundImage: 'none',
            borderRadius: 8,
            padding: '8px 18px',
            fontSize: 13,
            fontWeight: 600,
            color: '#FFFFFF',
            outline: 'none',
            boxShadow: 'none',
            textTransform: 'none'
        },
        buttonBack: {
            color: theme.palette.text.secondary,
            fontSize: 13,
            fontWeight: 500,
            marginRight: 8,
            textTransform: 'none'
        },
        buttonSkip: {
            color: theme.palette.text.secondary,
            fontSize: 13,
            fontWeight: 500,
            textTransform: 'none'
        },
        buttonClose: {
            color: theme.palette.text.secondary,
            top: 12,
            right: 12,
            height: 14,
            width: 14
        },
        spotlight: {
            borderRadius: 10
        }
    };
}

// Top AppBar (~64px) + fixed BreadcrumbBar (~64px) sit above the page content,
// so a target has to clear this much to count as visible.
const HEADER_OFFSET = 140;

// Bring a step's target into view — but ONLY when it isn't already there.
//
// Joyride scrolls on every step of its own accord: scrollToStep() runs whenever
// the lifecycle enters BEACON/TOOLTIP and animates the page to
// `target.top - scrollOffset` even when the element is fully visible, then
// re-places the tooltip 10ms after the animation settles. On a dense screen
// (Container's identity → quantities → lineage all sit within one card) that
// pair of movements per Next reads as the screen "shaking". We turn Joyride's
// scroller off via disableScrolling and do the minimum here instead.
//
// The measurement waits two frames: the entering step's training data and any
// tab switch commit in the same React commit as the index move, and the target
// may only mount in that commit.
function scrollTargetIntoViewIfNeeded(selector) {
    if (typeof selector !== 'string' || selector === '') return;
    if (typeof window === 'undefined') return;
    window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
            let el = null;
            try {
                el = document.querySelector(selector);
            } catch {
                return;   // a malformed selector from a guide row must not break the tour
            }
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const bottomLimit = window.innerHeight - 24;
            // Fully visible below the fixed headers → leave the page alone.
            if (rect.top >= HEADER_OFFSET && rect.bottom <= bottomLimit) return;
            const top = Math.max(0, window.scrollY + rect.top - HEADER_OFFSET);
            window.scrollTo({ top, behavior: 'smooth' });
        });
    });
}

const JOYRIDE_LOCALE = {
    back: 'Back',
    close: 'Close',
    last: 'Finish',
    next: 'Next',
    skip: 'Skip Tour'
};

// Module-level pub/sub so any screen (e.g., Item.jsx) can subscribe to per-step
// training-data updates without prop-drilling through the shared ActionBar.
// The hook publishes on the 'tooltip' lifecycle when training mode is active.
const stepStateListeners = new Set();
const tourLifecycleListeners = new Set();

// Joyride hands a helpers object (next, prev, go, close, skip, reset) to the
// `getHelpers` prop on mount. We stash it here at module scope so screen
// click handlers can programmatically advance the tour via advanceTour() —
// the canonical "click here → next" UX for tours that include click-driven
// navigation (e.g. opening a room dialog and continuing inside it). Cleared
// when the tour ends so screens can't accidentally drive a stale helper.
let tourHelpersRef = null;

export function getTourHelpers() {
    return tourHelpersRef;
}

// Public: advance the tour to the next step, if a tour is currently active.
// Safe to call from any screen handler — no-ops when no tour is running.
// Most screens should prefer the useAdvanceOnStep(stepId) hook in useTour.js
// which guards against advancing from the wrong step.
export function advanceTour() {
    tourHelpersRef?.next?.();
}

export function subscribeToStepState(fn) {
    stepStateListeners.add(fn);
    return () => stepStateListeners.delete(fn);
}

export function subscribeToTourLifecycle(fn) {
    tourLifecycleListeners.add(fn);
    return () => tourLifecycleListeners.delete(fn);
}

function emitStepState(payload) {
    stepStateListeners.forEach(fn => {
        try { fn(payload); } catch { /* listeners must not break the tour */ }
    });
}

function emitTourLifecycle(event) {
    tourLifecycleListeners.forEach(fn => {
        try { fn(event); } catch { /* listeners must not break the tour */ }
    });
}

const guideHelper = {

    useGuide(screenId, options = {}) {
        const {
            autoStart = true,
            // When true, skip the auto-load on mount — caller invokes loadGuide()
            // explicitly (e.g., when a help menu is opened). The welcome-dialog
            // flow is also bypassed since the user is launching the tour by
            // intent, not by passive screen visit.
            lazy = false,
            onTrainingComplete = null,
            onTrainingStart = null,
            onStepState = null
        } = options;

        const theme = useTheme();
        const dispatch = useDispatch();
        const joyrideStyles = useMemo(() => buildJoyrideStyles(theme), [theme]);

        // The "this screen has no tour" state. Applied on navigation and on a
        // 404 so a guide can never outlive the screen it was fetched for.
        const NO_GUIDE = useMemo(() => ({
            guide: null,
            guideProgress: null,
            shouldShowGuide: false,
            isGuideRunning: false,
            guideSteps: [],
            stepIndex: 0,
            _trainingMode: false
        }), []);

        const [guideState, setGuideState] = useState({
            guide: null,
            guideProgress: null,
            shouldShowGuide: false,
            isGuideRunning: false,
            guideSteps: [],
            // Controlled JoyRide step index. We drive advancement ourselves so we
            // can commit a step's training data (which may reveal a tab-gated or
            // conditionally-rendered target) in the SAME render as the index move.
            stepIndex: 0,
            _trainingMode: false
        });

        const [confirmationDialog, setConfirmationDialog] = useState({
            open: false,
            title: '',
            content: '',
            onConfirm: null,
            onCancel: null
        });

        // Refs that let callbacks read current values without re-creating themselves
        // on every render, and protect against setState-after-unmount.
        const isMountedRef = useRef(true);
        const guideRef = useRef(null);
        const trainingModeRef = useRef(false);
        const startTimerRef = useRef(null);
        const loadingRef = useRef(false);
        const loadedScreenRef = useRef(null);
        const abortRef = useRef(null);
        // Which screen the in-flight load is for. Needed because screenId can
        // change while a fetch is still running — see loadGuide().
        const inFlightScreenRef = useRef(null);
        // Direction of travel: +1 forward, -1 backward. A target_not_found
        // safety-skip has to continue the way the user was already heading.
        //
        // This used to hold the last index we navigated TO and the skip inferred
        // direction via `navDirRef.current < index`. That comparison is always
        // false at the moment it matters — Joyride reports the missing target for
        // the very index we just moved to, so stored === index — which pinned the
        // skip to +1. Forward worked by luck; backward bounced the user forward
        // again onto the step they had just left, so any tour with an absent
        // target could not be navigated back past it.
        const navDirRef = useRef(1);
        // Guards the finish side-effects (lifecycle emit + complete/dismiss POST)
        // so they run exactly once per tour, whether finish comes from walking off
        // the last step or from Joyride's own tour:end/skip/close event.
        const finishedRef = useRef(false);
        // The record id in the URL — the real record the user has open, which a
        // detail tour is about to hide behind a simulated one. Mirrored into a
        // ref because handleJoyrideCallback only re-creates on screenId change.
        // Every detail route in Routes.js names this param `id`.
        const { id: routeRecordId } = useParams();
        const routeRecordIdRef = useRef(routeRecordId);
        useEffect(() => { routeRecordIdRef.current = routeRecordId; }, [routeRecordId]);

        // Pin callback options to refs so we don't have to thread them through
        // useCallback dep arrays (and risk stale-closure churn).
        const optionsRef = useRef({ onTrainingComplete, onTrainingStart, onStepState });
        useEffect(() => {
            optionsRef.current = { onTrainingComplete, onTrainingStart, onStepState };
        }, [onTrainingComplete, onTrainingStart, onStepState]);

        // Mirror guide + training mode into refs whenever state changes.
        useEffect(() => {
            guideRef.current = guideState.guide;
            trainingModeRef.current = guideState._trainingMode === true;
        }, [guideState.guide, guideState._trainingMode]);

        // Mirror the (sorted, training-augmented) steps array into a ref so
        // handleJoyrideCallback's publishUpcomingStep can read the current
        // value — the callback itself only re-creates on screenId change,
        // so reading guideState.guideSteps from the closure would be stale.
        const stepsRef = useRef([]);
        useEffect(() => {
            stepsRef.current = guideState.guideSteps || [];
        }, [guideState.guideSteps]);

        // Publish run state to redux so:
        //   1. BreadcrumbBar can pin its AppBar open — otherwise HideOnScroll
        //      slides the help-button (and welcome breadcrumb) targets off-
        //      screen mid-tour and Joyride loses them.
        //   2. tourInterceptor.js can short-circuit axios GETs with mock
        //      responses for hub-style screens whose tour shouldn't show
        //      empty widgets on a fresh tenant.
        // The activeTourScreen value is the screenId the tour was loaded
        // against — tourInterceptor.js uses it to pick the right mock map.
        useEffect(() => {
            const running = !!guideState.isGuideRunning;
            dispatch({ type: SET_TOUR_RUNNING, payload: running });
            dispatch({ type: SET_ACTIVE_TOUR_SCREEN, payload: running ? screenId : null });
            return () => {
                if (guideState.isGuideRunning) {
                    dispatch({ type: SET_TOUR_RUNNING, payload: false });
                    dispatch({ type: SET_ACTIVE_TOUR_SCREEN, payload: null });
                }
            };
        }, [dispatch, guideState.isGuideRunning, screenId]);

        // Mount/unmount lifecycle: cancel in-flight requests, clear timers,
        // and stop any subsequent setState calls.
        useEffect(() => {
            isMountedRef.current = true;
            return () => {
                isMountedRef.current = false;
                if (startTimerRef.current) {
                    clearTimeout(startTimerRef.current);
                    startTimerRef.current = null;
                }
                if (abortRef.current) {
                    abortRef.current.abort();
                    abortRef.current = null;
                }
            };
        }, []);

        const safeSetGuideState = useCallback((updater) => {
            if (!isMountedRef.current) return;
            setGuideState(updater);
        }, []);

        const startGuide = useCallback(async () => {
            const guide = guideRef.current;
            if (!guide?._id) return;

            if (trainingModeRef.current && optionsRef.current.onTrainingStart) {
                optionsRef.current.onTrainingStart();
            }

            // Close any open welcome dialog — user has bypassed it (likely via
            // the Help menu's "Take Tour" entry) and is starting the tour now.
            setConfirmationDialog(prev => prev.open ? { ...prev, open: false } : prev);
            navDirRef.current = 1;   // a fresh tour always starts heading forward
            finishedRef.current = false;
            safeSetGuideState(prev => ({ ...prev, isGuideRunning: true, stepIndex: 0 }));
            // Joyride's scrollToFirstStep is off with disableScrolling, so bring
            // the opening target into view ourselves if the user started the tour
            // from further down the page.
            scrollTargetIntoViewIfNeeded(stepsRef.current?.[0]?.target);

            try {
                await axios.post(`/api/guides/${guide._id}/start`);
            } catch (error) {
                console.error('Error starting guide:', error);
            }
        }, [safeSetGuideState]);

        const dismissGuide = useCallback(async (reason = 'user_dismissed') => {
            const guide = guideRef.current;
            if (!guide?._id) return;

            safeSetGuideState(prev => ({
                ...prev,
                isGuideRunning: false,
                shouldShowGuide: false
            }));

            try {
                await axios.post(`/api/guides/${guide._id}/dismiss`, { reason });
            } catch (error) {
                console.error('Error dismissing guide:', error);
            }
        }, [safeSetGuideState]);

        const showWelcomeDialog = useCallback((guideName) => {
            if (!isMountedRef.current) return;
            setConfirmationDialog({
                open: true,
                title: 'Welcome Tutorial',
                content: `Welcome! We have a quick ${guideName} tutorial available to help you get started.\n\nWould you like to take the tour now? It will guide you through the key features step by step.`,
                onConfirm: () => {
                    if (!isMountedRef.current) return;
                    setConfirmationDialog(prev => ({ ...prev, open: false }));
                    startTimerRef.current = setTimeout(() => {
                        startTimerRef.current = null;
                        startGuide();
                    }, 500);
                },
                onCancel: () => {
                    if (!isMountedRef.current) return;
                    setConfirmationDialog(prev => ({ ...prev, open: false }));
                    dismissGuide('user_declined_welcome');
                }
            });
        }, [startGuide, dismissGuide]);

        const loadGuide = useCallback(async () => {
            if (!screenId) return;
            if (loadedScreenRef.current === screenId) return;
            // Only bail when a load for THIS screen is already running. Bailing
            // on any in-flight load made the hook permanently one navigation
            // behind: landing on screen B fires the effect once with A's
            // screenId (redux's help.current updates a tick later), and the
            // follow-up run for B hit `loadingRef.current === true` and
            // returned with nothing scheduled to retry. The result was that B
            // kept showing A's guide — the help menu offered the previous
            // screen's tour, and the welcome dialog auto-started it against a
            // screen whose targets don't exist.
            if (loadingRef.current && inFlightScreenRef.current === screenId) return;

            loadingRef.current = true;
            inFlightScreenRef.current = screenId;

            // Cancel any prior in-flight load and start a fresh one.
            if (abortRef.current) abortRef.current.abort();
            abortRef.current = new AbortController();
            const { signal } = abortRef.current;

            try {
                const response = await axios.get(`/api/guides/screen/${screenId}`, { signal });

                if (!response?.data || !isMountedRef.current) return;

                const incomingGuide = response.data.guide;
                let sortedSteps = incomingGuide?.steps
                    ? [...incomingGuide.steps].sort((a, b) => a.order - b.order)
                    : [];

                const isTraining = trainingDataLoader.shouldUseTrainingData(incomingGuide, screenId);

                if (isTraining) {
                    // Pull the screen's training module on demand. Returns
                    // instantly if already cached; awaits a dynamic import on
                    // first use so the modules stay out of the entry chunk.
                    await trainingDataLoader.ensureLoaded(screenId);
                    if (!isMountedRef.current) return;
                    sortedSteps = sortedSteps.map((step, index) => {
                        const stepNumber = index + 1;
                        const trainingData = trainingDataLoader.loadTrainingDataForGuide(
                            incomingGuide, screenId, stepNumber
                        );
                        if (!trainingData) return step;
                        const { state: _removedDbState, ...stepWithoutState } = step;
                        return { ...stepWithoutState, state: trainingData };
                    });
                }

                const newState = {
                    guide: incomingGuide,
                    guideProgress: response.data.progress,
                    shouldShowGuide: response.data.shouldShow,
                    guideSteps: sortedSteps,
                    _trainingMode: response.data.shouldShow || isTraining
                };

                safeSetGuideState(prev => ({ ...prev, ...newState }));
                loadedScreenRef.current = screenId;

                if (autoStart && newState.shouldShowGuide && newState.guide) {
                    const hasCompletedSteps = newState.guideProgress?.completedSteps?.length > 0;
                    if (!newState.guideProgress || !hasCompletedSteps) {
                        showWelcomeDialog(newState.guide.name || 'Tutorial');
                    } else {
                        startTimerRef.current = setTimeout(() => {
                            startTimerRef.current = null;
                            startGuide();
                        }, 500);
                    }
                }
            } catch (error) {
                if (axios.isCancel?.(error) || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
                    return;
                }
                // A screen with no guide is answered with 404, not an empty body.
                // Left unhandled, this branch returned without touching state, so
                // the PREVIOUS screen's guide stayed loaded — the Help Center on
                // Rooms went on offering the Home dashboard tour. Clear it.
                if (error?.response?.status === 404) {
                    if (isMountedRef.current) safeSetGuideState(prev => ({ ...prev, ...NO_GUIDE }));
                    loadedScreenRef.current = screenId;
                    return;
                }
                console.error('Error loading guide:', error);
            } finally {
                // Only the newest load clears the flag. An older, aborted load
                // settling later must not unlock the guard out from under the
                // request that superseded it.
                if (inFlightScreenRef.current === screenId) loadingRef.current = false;
            }
        }, [screenId, autoStart, safeSetGuideState, showWelcomeDialog, startGuide, NO_GUIDE]);

        const handleJoyrideCallback = useCallback(async (data) => {
            const { action, index, status, type, step, lifecycle } = data;
            const guide = guideRef.current;
            if (!guide?._id) return;

            const isTrainingMode = trainingModeRef.current;
            const screenIdForTraining = guide.screenId || screenId;
            const { onStepState, onTrainingComplete } = optionsRef.current;

            // Helper: push a training-data snapshot through reducer + subscribers
            // so consumers can render the upcoming step's lifecycle state before
            // Joyride searches for its target. Subscribers (e.g. CultivationBatch's
            // SET_RECORD listener) need this preload to render conditionally-mounted
            // elements like cb-processing / cb-inventory; otherwise Joyride looks for
            // them before they exist in the DOM and silently skips the step.
            const applyTrainingForStep = (stepNumber) => {
                if (!isTrainingMode) return;
                const trainingData = trainingDataLoader.loadTrainingData(screenIdForTraining, stepNumber);
                if (!trainingData) return;
                // Publish the id swap this step performs. Widgets that show real
                // user data and are never simulated — comments, attachments,
                // print — read this pair through useRecordIdForFetch so they
                // keep fetching the record in the URL rather than the mock id.
                // Published from here rather than from useTour because eight
                // screens (Sale, Invoice, Car, Incident, Sop, PurchaseOrder,
                // ProductionBatch, TestResults) still hand-roll their own step
                // subscription and never call useTour.
                dispatch({
                    type: SET_TOUR_RECORD_IDS,
                    payload: {
                        mockId: trainingData.record?._id || null,
                        realId: routeRecordIdRef.current || null
                    }
                });
                safeSetGuideState(prev => ({ ...prev, ...trainingData }));
                onStepState?.(trainingData);
                emitStepState(trainingData);
            };

            // Controlled navigation. Joyride is given `stepIndex` and does NOT
            // auto-advance — WE move it here. We preload the entering step's
            // training data + publish its id, then set stepIndex, all in one
            // React commit. So a tab-gated / conditionally-rendered target
            // (pb-outputs, cb-processing, room-insight cards…) is in the DOM and
            // visible BEFORE Joyride re-resolves at the new index. Uncontrolled
            // mode evaluated the next target before this callback ran, so forward
            // nav skipped such targets as target_not_found (elFound but hidden).
            // Finish side-effects, run exactly once: stop the tour, notify
            // subscribers (the screen restores its real record off this 'end'
            // event), and record completion/dismissal. Reached either by walking
            // off the last step or by Joyride's own tour:end/skip/close event.
            const finishTour = async (finishStatus) => {
                if (finishedRef.current) return;
                finishedRef.current = true;
                safeSetGuideState(prev => ({ ...prev, isGuideRunning: false, stepIndex: 0 }));
                // Clear cached helpers so a stray post-tour advanceTour() is a safe
                // no-op, not a phantom dispatch into a now-unmounted Joyride.
                tourHelpersRef = null;
                emitTourLifecycle({ type: 'end', status: finishStatus, screenId: screenIdForTraining });
                // After the 'end' listeners have put the real _id back on the
                // screen — until then the swap has to stay published so the
                // widgets don't briefly see the mock id again.
                dispatch({ type: SET_TOUR_RECORD_IDS, payload: { mockId: null, realId: null } });
                try {
                    if (finishStatus === 'finished') {
                        await axios.post(`/api/guides/${guide._id}/complete`);
                    } else {
                        await axios.post(`/api/guides/${guide._id}/dismiss`, {
                            reason: finishStatus === 'skipped' ? 'user_skipped' : 'user_closed'
                        });
                    }
                    if (isTrainingMode) onTrainingComplete?.();
                } catch { /* completion tracking is best-effort */ }
            };

            const goTo = (nextIndex, dir) => {
                const total = stepsRef.current?.length || 0;
                if (typeof dir === 'number') navDirRef.current = dir;
                if (nextIndex < 0) return;
                if (nextIndex >= total) {
                    // Walked off the last step → finish (and tear down mock data).
                    finishTour('finished');
                    return;
                }
                applyTrainingForStep(nextIndex + 1);     // STEP_STATES keys are 1-based
                const upcoming = stepsRef.current?.[nextIndex];
                dispatch({ type: SET_ACTIVE_TOUR_STEP, payload: upcoming?.id || null });
                safeSetGuideState(prev => ({ ...prev, stepIndex: nextIndex }));
                scrollTargetIntoViewIfNeeded(upcoming?.target);
            };

            try {
                // Re-apply the current step's mock state once its tooltip is up
                // (safety net / single source of truth) and publish its id so
                // screen click handlers can drive click-to-advance UX.
                if (lifecycle === 'tooltip' && step) {
                    applyTrainingForStep(index + 1);
                    dispatch({ type: SET_ACTIVE_TOUR_STEP, payload: step.id || null });
                }

                // Tour finished / skipped / closed (Joyride's own end event, the
                // Skip button, or the last-step "Done" which arrives as a close).
                if (type === 'tour:end' || action === 'skip' || action === 'close') {
                    const finishStatus = status === 'finished' ? 'finished'
                        : status === 'skipped' || action === 'skip' ? 'skipped'
                        : 'closed';
                    await finishTour(finishStatus);
                    return;
                }

                // Step completed via Next / Back — advance the controlled index.
                if (type === 'step:after' && (action === 'next' || action === 'prev')) {
                    const dir = action === 'prev' ? -1 : 1;
                    goTo(index + dir, dir);
                    axios.post(`/api/guides/${guide._id}/progress`, {
                        stepId: step?.id || `step-${index}`,
                        stepIndex: index,
                        interaction: { action, timestamp: new Date(), data: { type, status, lifecycle } }
                    }).catch(() => { /* progress is best-effort */ });
                    return;
                }

                // A target truly couldn't be resolved — skip it in the direction
                // we were heading so the tour never gets stuck. With the preload
                // above this is rare; it only catches genuinely absent targets.
                if (type === 'error:target_not_found') {
                    // Keep going the way the user was heading, so a step whose
                    // target is legitimately absent on this screen is stepped
                    // over in both directions rather than bounced off.
                    goTo(index + navDirRef.current, navDirRef.current);
                    return;
                }
            } catch (error) {
                console.error('Error handling guide callback:', error);
            }
        }, [screenId, safeSetGuideState, dispatch]);

        const getJoyrideProps = useCallback(() => {
            // Force disableBeacon on every step so clicking "Start" in the welcome
            // dialog steps straight into the first tooltip instead of showing a
            // pulsing dot users have to click. Continuous mode handles the rest.
            const steps = (guideState.guideSteps || []).map(s => ({
                ...s,
                disableBeacon: true
            }));
            return {
                steps,
                run: guideState.isGuideRunning || false,
                // Controlled: advancement is driven from handleJoyrideCallback's
                // goTo() so a step's training data commits before Joyride resolves
                // the (possibly tab-gated) target at the new index.
                stepIndex: guideState.stepIndex || 0,
                continuous: true,
                showProgress: true,
                showSkipButton: true,
                scrollToFirstStep: false,
                // Joyride's scroller re-scrolls on EVERY step (and re-places the
                // tooltip right after), even when the target never left the
                // viewport — the per-step shake. goTo() calls
                // scrollTargetIntoViewIfNeeded() instead, which moves the page
                // only when the next target is actually off-screen.
                disableScrolling: true,
                // Stop Joyride from temporarily mutating the scroll parent's
                // styles to recompute target geometry on each step. That toggle
                // is a one-frame layout shift that fires on EVERY step advance
                // (even when the target is already on-screen), which reads as a
                // screen "shake". Our scroll parent is the normal window, so the
                // parent-fix buys us nothing and only costs the jitter.
                disableScrollParentFix: true,
                // Capture Joyride's helpers object (next/prev/go/close/skip)
                // into module scope so advanceTour() / useAdvanceOnStep can
                // programmatically drive the tour from screen click handlers.
                getHelpers: (helpers) => { tourHelpersRef = helpers; },
                // Only consulted if a guide row ever re-enables scrolling for a
                // single step; our own scroller uses the same offset.
                scrollOffset: HEADER_OFFSET,
                // Clicking off the spotlight cancels the tour and hands the user
                // back the record they were on — handleJoyrideCallback sees
                // action 'close', which runs finishTour, which emits the 'end'
                // lifecycle that useTour listens for to restore the real _id and
                // refetch. Previously this was `disableOverlayClose: isFirstTime`,
                // so a first-time user's click on the overlay was swallowed
                // entirely: the tour stayed up with no way out but the Skip
                // button (hideCloseButton is on, so there is no X). Clicking away
                // is how people leave things, and it should mean "I'm done", not
                // nothing.
                disableOverlayClose: false,
                hideBackButton: false,
                hideCloseButton: true,
                disableBeacon: true,
                spotlightPadding: 6,
                floaterProps: { disableAnimation: false },
                callback: handleJoyrideCallback,
                locale: JOYRIDE_LOCALE,
                styles: joyrideStyles
            };
        }, [guideState.guideSteps, guideState.isGuideRunning, guideState.stepIndex, handleJoyrideCallback, joyrideStyles]);

        const shouldRenderGuide = !!(guideState.guide && guideState.guideSteps && guideState.guideSteps.length > 0);

        // Trigger initial load when screenId becomes available or changes —
        // skipped entirely in lazy mode so the caller controls when to fetch.
        useEffect(() => {
            if (screenId && !lazy) loadGuide();
        }, [screenId, lazy, loadGuide]);

        // When the active screen changes (e.g., user navigates away mid-tour),
        // stop any in-progress tour and clear the load cache so the next
        // loadGuide() fetches the new screen's guide instead of returning early.
        const prevScreenIdRef = useRef(screenId);
        useEffect(() => {
            if (prevScreenIdRef.current !== screenId) {
                loadedScreenRef.current = null;
                // Navigating away is an exit like any other, so it has to run
                // the same teardown the Skip button and the overlay click do.
                // Clearing isGuideRunning on its own only hides the tooltip: the
                // 'end' lifecycle never fires, so useTour never restores the
                // screen's real _id, and the simulated record stays in the
                // reducer with its mock ids still wired into every child widget.
                if (guideState.isGuideRunning && !finishedRef.current) {
                    finishedRef.current = true;
                    tourHelpersRef = null;
                    emitTourLifecycle({
                        type: 'end',
                        status: 'closed',
                        screenId: guideRef.current?.screenId || prevScreenIdRef.current
                    });
                    dispatch({ type: SET_TOUR_RECORD_IDS, payload: { mockId: null, realId: null } });
                    const guideId = guideRef.current?._id;
                    if (guideId != null) {
                        axios.post(`/api/guides/${guideId}/dismiss`, { reason: 'user_navigated' })
                            .catch(() => { /* dismissal tracking is best-effort */ });
                    }
                }
                // Drop the whole guide, not just the running flag. Keeping the
                // steps around meant the new screen briefly — or permanently,
                // if its own fetch 404s — advertised the previous screen's tour.
                safeSetGuideState(prev => ({ ...prev, ...NO_GUIDE }));
            }
            prevScreenIdRef.current = screenId;
        }, [screenId, safeSetGuideState, NO_GUIDE, guideState.isGuideRunning, dispatch]);

        const confirmationDialogProps = {
            open: confirmationDialog.open,
            title: confirmationDialog.title,
            content: confirmationDialog.content,
            yesButton: 'Start Tutorial',
            noButton: 'Maybe Later',
            yes: confirmationDialog.onConfirm,
            no: confirmationDialog.onCancel,
            toggleDialog: confirmationDialog.onCancel
        };

        return {
            guideState,
            setGuideState: safeSetGuideState,
            loadGuide,
            startGuide,
            dismissGuide,
            handleJoyrideCallback,
            getJoyrideProps,
            shouldRenderGuide,
            confirmationDialogProps
        };
    }
};

export default guideHelper;
